import React from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';

import CampaignTypeChip from '../DealInformation/shared/CampaignTypeChip';

import ContextDetail from './ContextDetail';
import ContextId from './ContextId';
import CrmResyncButton from './CrmResyncButton';
import { CAMPAIGN_ASSESSMENTS_URL, CHECKLIST_FLASH_HOTELS_URL } from './constants';
import { formatContextDate } from './shared/checklistUtils';

interface Props {
  approvedCount: number;
  bookByDate?: string;
  campaignType?: string;
  channelManager?: string;
  hotelName: string;
  needsApprovalCount: number;
  offerId: string;
  salesforceOpportunityId?: string;
  travelToDate?: string;
  totalCount: number;
  onSelectApprovalQueue: () => void;
}

export default function ContextCard({
  approvedCount,
  bookByDate,
  campaignType,
  channelManager,
  hotelName,
  needsApprovalCount,
  offerId,
  salesforceOpportunityId,
  travelToDate,
  totalCount,
  onSelectApprovalQueue,
}: Props) {
  const approvedPercent = totalCount > 0 ? Math.round((approvedCount / totalCount) * 100) : 0;
  const contextDetails = [
    { label: 'Travel', value: formatContextDate(travelToDate) },
    { label: 'Book by', value: formatContextDate(bookByDate) },
    { label: 'Channel manager', value: channelManager || 'Not available' },
  ];

  return (
    <Paper sx={{ position: 'relative', overflow: 'hidden', p: { xs: 1.5, md: 2 } }} variant="outlined">
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 4,
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        }}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" component="h2" fontWeight={600}>
              {hotelName}
            </Typography>
            {campaignType && <CampaignTypeChip campaignType={campaignType} />}
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
            <ContextId label="Offer ID" value={offerId} />
            {contextDetails.map((detail) => (
              <ContextDetail key={detail.label} label={detail.label} value={detail.value} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.4 }}>
              Rules:
            </Typography>
            <Link
              href={CAMPAIGN_ASSESSMENTS_URL}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 600 }}
            >
              Campaign Assessments <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
            <Link
              href={CHECKLIST_FLASH_HOTELS_URL}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 600 }}
            >
              Checklist Flash Hotels <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
          </Stack>
        </Box>

        <Stack spacing={1} alignItems={{ xs: 'center', md: 'flex-end' }} sx={{ flexShrink: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress size={56} thickness={4} variant="determinate" value={100} sx={{ color: 'grey.200' }} />
              <CircularProgress
                size={56}
                thickness={4}
                variant="determinate"
                value={approvedPercent}
                sx={{ position: 'absolute', left: 0, color: 'primary.main' }}
              />
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                <Typography variant="caption" fontWeight={700}>
                  {approvedPercent}%
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                {approvedCount}
                <Box component="span" sx={{ color: 'grey.400', fontWeight: 400 }}>
                  /{totalCount} approved
                </Box>
              </Typography>
              <Button
                color="warning"
                size="small"
                startIcon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  minWidth: 0,
                  p: 0,
                  textTransform: 'none',
                }}
                variant="text"
                onClick={onSelectApprovalQueue}
              >
                {needsApprovalCount} need review
              </Button>
            </Box>
          </Stack>
          <CrmResyncButton opportunityId={salesforceOpportunityId} size="small" />
        </Stack>
      </Stack>
    </Paper>
  );
}
